// Objective.js
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import {
  PlusIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  BeakerIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import VetStartedSOAPSection from "./VetStartedSOAPSection";
import { ChevronDownIcon } from "lucide-react";
import { usePrescription } from "./PrescriptionContext";
import { useBilling } from "./BillingContext";
import config from "../../config";

// ─── Shared micro-components ──────────────────────────────────────────────────

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

const ChipGroup = ({ value, options, onChange, multi = false, color = "teal" }) => {
  const selected = multi ? (Array.isArray(value) ? value : []) : value;
  const isActive = (opt) => (multi ? selected.includes(opt) : selected === opt);

  const toggle = (opt) => {
    if (multi) {
      const arr = Array.isArray(selected) ? selected : [];
      onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    } else {
      onChange(isActive(opt) ? "" : opt);
    }
  };

  const activeClass =
    color === "teal"
      ? "bg-teal-600 border-teal-600 text-white"
      : "bg-indigo-600 border-indigo-600 text-white";
  const hoverClass =
    color === "teal"
      ? "hover:border-teal-300 hover:text-teal-700"
      : "hover:border-indigo-300 hover:text-indigo-700";

  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition-all ${
            isActive(opt)
              ? activeClass
              : `bg-white border-slate-200 text-slate-600 ${hoverClass}`
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
};

const SelectField = ({ label, value, options, onChange, placeholder }) => (
  <div>
    {label && <FieldLabel>{label}</FieldLabel>}
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none text-[12px] text-slate-700 border border-slate-200 rounded-xl px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-transparent bg-white"
      >
        <option value="">{placeholder || "Select…"}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  </div>
);

const NumberInput = ({ label, value, onChange, placeholder, unit }) => (
  <div>
    {label && <FieldLabel>{label}</FieldLabel>}
    <div className="relative">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "0"}
        className="w-full text-[12px] text-slate-700 border border-slate-200 rounded-xl px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-transparent bg-white"
      />
      {unit && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-semibold">
          {unit}
        </span>
      )}
    </div>
  </div>
);

const NoteField = ({ label, value, onChange, placeholder, rows = 2 }) => (
  <div>
    {label && <FieldLabel>{label}</FieldLabel>}
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full text-[12px] text-slate-700 border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-transparent placeholder-slate-300 bg-white leading-relaxed"
    />
  </div>
);

// ─── Vitals Section ───────────────────────────────────────────────────────────

const AddVitalsModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    weight: "", temp: "", hr: "", rr: "", bcs: "", painScore: "",
  });
  const p = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 text-sm">Record Vitals</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <XMarkIcon className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <NumberInput label="Weight" value={form.weight} onChange={p("weight")} placeholder="0.0" unit="kg" />
          <NumberInput label="Temperature" value={form.temp} onChange={p("temp")} placeholder="38.5" unit="°F" />
          <NumberInput label="Heart Rate" value={form.hr} onChange={p("hr")} placeholder="80" unit="bpm" />
          <NumberInput label="Respiratory Rate" value={form.rr} onChange={p("rr")} placeholder="20" unit="rpm" />
          <div>
            <FieldLabel>BCS (1–9)</FieldLabel>
            <div className="flex gap-1 flex-wrap">
              {[1,2,3,4,5,6,7,8,9].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, bcs: String(n) }))}
                  className={`w-7 h-7 text-[11px] font-bold rounded-lg border transition-all ${
                    form.bcs === String(n)
                      ? "bg-teal-600 border-teal-600 text-white"
                      : "border-slate-200 text-slate-600 hover:border-teal-300"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <SelectField
            label="Pain Score"
            value={form.painScore}
            onChange={p("painScore")}
            options={["0 – None", "1 – Mild", "2 – Moderate", "3 – Severe", "4 – Excruciating"]}
          />
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-[12px] font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">
            Cancel
          </button>
          <button
            onClick={() => { onSave(form); onClose(); }}
            className="px-4 py-2 text-[12px] font-semibold bg-teal-600 text-white rounded-xl hover:bg-teal-700"
          >
            Save Vitals
          </button>
        </div>
      </div>
    </div>
  );
};

const VitalsTable = ({ rows, onAdd }) => {
  if (rows.length === 0) {
    return (
      <button
        type="button"
        onClick={onAdd}
        className="w-full border-2 border-dashed border-slate-200 rounded-xl py-3 text-center hover:border-teal-300 hover:bg-teal-50/30 transition-colors"
      >
        <p className="text-[11px] text-slate-400 hover:text-teal-600">
          No vitals recorded — tap to add
        </p>
      </button>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-100">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-slate-50 text-slate-500">
            {["Date", "Time", "Weight", "Temp", "HR", "RR", "BCS", "Pain"].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-bold text-[10px] uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 text-slate-700">
              <td className="px-3 py-2">{r.date}</td>
              <td className="px-3 py-2">{r.time}</td>
              <td className="px-3 py-2">{r.weight ? `${r.weight} kg` : "—"}</td>
              <td className="px-3 py-2">{r.temp ? `${r.temp}°F` : "—"}</td>
              <td className="px-3 py-2">{r.hr ? `${r.hr} bpm` : "—"}</td>
              <td className="px-3 py-2">{r.rr ? `${r.rr} rpm` : "—"}</td>
              <td className="px-3 py-2">{r.bcs || "—"}</td>
              <td className="px-3 py-2">{r.painScore || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Clinical Exam Tabs ───────────────────────────────────────────────────────

const EXAM_TABS = [
  { id: "general", label: "General" },
  { id: "eyes", label: "Eyes" },
  { id: "ears", label: "Ears" },
  { id: "oral", label: "Oral/Dental" },
  { id: "skin", label: "Skin & Coat" },
  { id: "cardiovascular", label: "Cardiovascular" },
  { id: "respiratory", label: "Respiratory" },
  { id: "gi", label: "GI" },
  { id: "musculoskeletal", label: "Musculoskeletal" },
  { id: "neurological", label: "Neurological" },
];

const GeneralExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Hydration" value={data.hydration || ""} onChange={p("hydration")}
        options={["Normal (< 5%)", "Mild (5–6%)", "Moderate (7–8%)", "Severe (> 8%)"]} />
      <div>
        <FieldLabel>Depressed?</FieldLabel>
        <ChipGroup value={data.depressed || ""} options={["Yes", "No"]} onChange={p("depressed")} />
      </div>
      <SelectField label="Mentation" value={data.mentation || ""} onChange={p("mentation")}
        options={["Alert & responsive", "Quiet / subdued", "Depressed", "Obtunded", "Stuporous", "Comatose"]} />
    </div>
  );
};

const EyesExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  const EYE_OPTIONS = ["Normal", "Discharge", "Conjunctivitis", "Corneal opacity", "Miosis", "Mydriasis", "Chemosis", "Ulceration"];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Left Eye" value={data.leftEye || ""} onChange={p("leftEye")} options={EYE_OPTIONS} />
      <SelectField label="Right Eye" value={data.rightEye || ""} onChange={p("rightEye")} options={EYE_OPTIONS} />
      <div>
        <FieldLabel>Discharge?</FieldLabel>
        <ChipGroup value={data.discharge || ""} options={["Yes", "No"]} onChange={p("discharge")} />
      </div>
      <div>
        <FieldLabel>Redness?</FieldLabel>
        <ChipGroup value={data.redness || ""} options={["Yes", "No"]} onChange={p("redness")} />
      </div>
    </div>
  );
};

const EarsExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  const EAR_OPTIONS = ["Normal", "Erythema", "Discharge (brown)", "Discharge (black)", "Discharge (yellow/pus)", "Odour", "Stenosis", "Haematoma"];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Left Ear" value={data.leftEar || ""} onChange={p("leftEar")} options={EAR_OPTIONS} />
      <SelectField label="Right Ear" value={data.rightEar || ""} onChange={p("rightEar")} options={EAR_OPTIONS} />
      <div>
        <FieldLabel>Discharge?</FieldLabel>
        <ChipGroup value={data.discharge || ""} options={["Yes", "No"]} onChange={p("discharge")} />
      </div>
      <div>
        <FieldLabel>Odour?</FieldLabel>
        <ChipGroup value={data.odour || ""} options={["Yes", "No"]} onChange={p("odour")} />
      </div>
    </div>
  );
};

const OralExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="CRT" value={data.crt || ""} onChange={p("crt")}
        options={["< 1s (fast)", "1–2s (normal)", "2–3s (slow)", "> 3s (very slow)"]} />
      <SelectField label="Mucous Membranes" value={data.mm || ""} onChange={p("mm")}
        options={["Pink", "Pale pink", "White", "Pale", "Blue/grey", "Yellow/icteric", "Brick red", "Muddy brown"]} />
      <SelectField label="Dental Grade" value={data.dentalGrade || ""} onChange={p("dentalGrade")}
        options={["Grade 0 – Normal", "Grade 1 – Mild plaque", "Grade 2 – Moderate tartar", "Grade 3 – Severe tartar", "Grade 4 – Advanced disease"]} />
    </div>
  );
};

const SkinExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  const COAT_CONDITIONS = ["Normal", "Dull", "Dry", "Greasy", "Matted", "Poor"];
  const ALOPECIA_OPTIONS = ["None", "Focal", "Multifocal", "Generalised"];
  const SKIN_CONDITIONS = ["None", "Erythema", "Papules", "Pustules", "Macules", "Plaques", "Nodules", "Ulceration", "Crusting", "Scaling", "Hyperpigmentation"];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Coat Condition" value={data.coatCondition || ""} onChange={p("coatCondition")} options={COAT_CONDITIONS} />
      <SelectField label="Hair Loss (Alopecia)" value={data.alopecia || ""} onChange={p("alopecia")} options={ALOPECIA_OPTIONS} />
      <div>
        <FieldLabel>Skin Findings</FieldLabel>
        <ChipGroup value={data.skinFindings || []} options={SKIN_CONDITIONS} onChange={p("skinFindings")} multi />
      </div>
      <div>
        <FieldLabel>Ticks present?</FieldLabel>
        <ChipGroup value={data.ticks || ""} options={["Not present", "Present"]} onChange={p("ticks")} />
      </div>
      <div>
        <FieldLabel>Lumps / Masses?</FieldLabel>
        <ChipGroup value={data.lumps || ""} options={["Not present", "Present"]} onChange={p("lumps")} />
      </div>
      <div>
        <FieldLabel>Wounds / Lesions?</FieldLabel>
        <ChipGroup value={data.wounds || ""} options={["Not present", "Present"]} onChange={p("wounds")} />
      </div>
    </div>
  );
};

const CardiovascularExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <NumberInput label="Heart Rate" value={data.hr || ""} onChange={p("hr")} placeholder="120" unit="bpm" />
      <SelectField label="Rhythm" value={data.rhythm || ""} onChange={p("rhythm")}
        options={["Regular", "Irregular", "Regularly irregular", "Irregularly irregular"]} />
      <SelectField label="Murmur" value={data.murmur || ""} onChange={p("murmur")}
        options={["None", "Grade I", "Grade II", "Grade III", "Grade IV", "Grade V", "Grade VI"]} />
    </div>
  );
};

const RespiratoryExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <NumberInput label="Respiratory Rate" value={data.rr || ""} onChange={p("rr")} placeholder="24" unit="rpm" />
      <SelectField label="Lung Sounds" value={data.lungSounds || ""} onChange={p("lungSounds")}
        options={["Clear bilaterally", "Increased", "Decreased", "Crackles", "Wheezes", "Referred upper airway", "Pleural friction rub", "Absent"]} />
    </div>
  );
};

const GIExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Appetite" value={data.appetite || ""} onChange={p("appetite")}
        options={["Normal", "Reduced", "Increased", "Absent"]} />
      <SelectField label="Abdominal Palpation" value={data.abdominal || ""} onChange={p("abdominal")}
        options={["Soft, non-painful", "Tense", "Painful on palpation", "Distended", "Mass palpable", "Organomegaly", "Gas-filled loops"]} />
    </div>
  );
};

const MusculoskeletalExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Gait" value={data.gait || ""} onChange={p("gait")}
        options={["Normal", "Stiff", "Ataxic", "Lame"]} />
      <SelectField label="Lameness" value={data.lameness || ""} onChange={p("lameness")}
        options={["0 – None", "1 – Intermittent, barely detectable", "2 – Mild, weight-bearing", "3 – Moderate, reduced weight-bearing", "4 – Severe, minimal weight-bearing", "5 – Non-weight-bearing"]} />
      <SelectField label="Affected Limb" value={data.affectedLimb || ""} onChange={p("affectedLimb")}
        options={["N/A", "Left Front", "Right Front", "Left Hind", "Right Hind", "Multiple"]} />
    </div>
  );
};

const NeurologicalExam = ({ data = {}, onChange }) => {
  const p = (k) => (v) => onChange({ ...data, [k]: v });
  const NEURO_OPTIONS = ["Normal", "Paresis", "Paralysis", "Ataxia", "Head tilt", "Nystagmus", "Seizure activity", "Reduced proprioception", "Absent reflexes"];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <SelectField label="Mentation" value={data.mentation || ""} onChange={p("mentation")}
        options={["Alert & responsive", "Quiet / subdued", "Depressed", "Obtunded", "Stuporous", "Comatose"]} />
      <SelectField label="Reflexes" value={data.reflexes || ""} onChange={p("reflexes")}
        options={["Normal", "Reduced", "Absent", "Exaggerated"]} />
      <div className="sm:col-span-2">
        <FieldLabel>Neurological Findings</FieldLabel>
        <ChipGroup value={data.findings || []} options={NEURO_OPTIONS} onChange={p("findings")} multi />
      </div>
    </div>
  );
};

const EXAM_PANELS = {
  general: GeneralExam,
  eyes: EyesExam,
  ears: EarsExam,
  oral: OralExam,
  skin: SkinExam,
  cardiovascular: CardiovascularExam,
  respiratory: RespiratoryExam,
  gi: GIExam,
  musculoskeletal: MusculoskeletalExam,
  neurological: NeurologicalExam,
};

const ClinicalExamTabs = ({ examFindings = {}, onExamFindingsChange }) => {
  const [activeTab, setActiveTab] = useState("general");
  const Panel = EXAM_PANELS[activeTab];

  return (
    <div>
      <div className="flex gap-1 overflow-x-auto pb-1 mb-3 scrollbar-none">
        {EXAM_TABS.map((tab) => {
          const tabData = examFindings[tab.id] || {};
          const hasData = Object.values(tabData).some((v) =>
            Array.isArray(v) ? v.length > 0 : !!v
          );
          return (
            <button
              key={tab.id}
              type="button"
              data-nav-tab="true"
              onClick={() => setActiveTab(tab.id)}
              className={`shrink-0 px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-all ${
                activeTab === tab.id
                  ? "bg-teal-600 border-teal-600 text-white"
                  : hasData
                  ? "bg-teal-50 border-teal-200 text-teal-700"
                  : "bg-white border-slate-200 text-slate-500 hover:border-teal-200"
              }`}
            >
              {tab.label}
              {hasData && activeTab !== tab.id && (
                <span className="ml-1 w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-4">
        <Panel
          data={examFindings[activeTab] || {}}
          onChange={(val) => onExamFindingsChange(activeTab, val)}
        />
      </div>
    </div>
  );
};

// ─── Lab Tests Section with Catalog Select + Per-Row Upload ─────────────────
const LabTestsSection = ({ markDirty }) => {
  const { labTests, setLabTests, generateError, canGenerate, isGenerating, generatePrescription } = usePrescription();

  const [availableLabTests, setAvailableLabTests] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploadTargetId, setUploadTargetId] = useState(null);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchLabTests = async () => {
      setLoadingCatalog(true);
      try {
        const response = await httpService.get("/vendorvetclinicservice/get-lab-tests");
        if (response.code === 200) {
          const active = (response.data || []).filter((t) => t.status !== "inactive");
          setAvailableLabTests(active);
        }
      } catch (err) {
        console.error("Failed to load lab test catalog:", err);
        toast.error("Failed to load lab tests");
      } finally {
        setLoadingCatalog(false);
      }
    };
    fetchLabTests();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const alreadyAddedIds = new Set(labTests.map((t) => t.lab_test_id));
    const results =
      term.length === 0
        ? availableLabTests.filter((t) => !alreadyAddedIds.has(t._id))
        : availableLabTests.filter(
            (t) =>
              !alreadyAddedIds.has(t._id) &&
              (t.name?.toLowerCase().includes(term) ||
                t.client_friendly_name?.toLowerCase().includes(term))
          );
    setSearchResults(results);
  }, [searchTerm, availableLabTests, labTests]);

  // ⭐ FIXED: Only add to labTests, NOT to billing
  const handleSelectLabTest = (test) => {
    if (labTests.some((t) => t.lab_test_id === test._id)) {
      toast.info("This lab test is already added");
      return;
    }

    const price = Number(test.price || 0);

    const newRow = {
      id: `lab-${Date.now()}`,
      lab_test_id: test._id,
      name: test.name,
      sampleType: test.sample_type || "",
      price,
      fileUrl: "",
      fileName: "",
    };

    // Only add to Objective / SOAP lab tests
    setLabTests((prev) => [...prev, newRow]);

    // Mark Objective dirty so SOAP autosave runs
    markDirty();

    setSearchTerm("");
    setShowDropdown(false);
  };

  // ⭐ FIXED: Only remove from labTests, NOT from billing
  const handleRemoveTest = (id) => {
    setLabTests((prev) => prev.filter((t) => t.id !== id));
    markDirty();
  };

  const handlePriceChange = (id, value) => {
    setLabTests((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, price: value } : t
      )
    );
    markDirty();
  };

  const triggerUpload = (rowId) => {
    setUploadTargetId(rowId);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadTargetId) return;

    setLabTests(labTests.map((t) => (t.id === uploadTargetId ? { ...t, uploading: true } : t)));

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("module", "vendors");
      formData.append("docType", "lab_reports");

      const response = await httpService.post("/upload/fileUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const s3Key = response?.data?.s3Key || response?.data?.url;
      if (s3Key) {
        const fileUrl = s3Key.startsWith("http")
          ? s3Key
          : `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${s3Key}`;

        setLabTests((prev) =>
          prev.map((t) =>
            t.id === uploadTargetId
              ? { ...t, fileUrl, fileName: file.name, uploading: false }
              : t
          )
        );
        toast.success(`Report "${file.name}" uploaded successfully`);
      } else {
        throw new Error("No file URL returned");
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      toast.error("Failed to upload report");
      setLabTests((prev) =>
        prev.map((t) => (t.id === uploadTargetId ? { ...t, uploading: false } : t))
      );
      markDirty();
    } finally {
      setUploadTargetId(null);
    }
  };

  const handleRemoveFile = (rowId) => {
    setLabTests(labTests.map((t) => (t.id === rowId ? { ...t, fileUrl: "", fileName: "" } : t)));
  };

  const handleViewFile = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const totalLabCost = labTests.reduce((sum, t) => sum + (Number(t.price) || 0), 0);

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <FieldLabel>Select Lab Test</FieldLabel>
          {canGenerate && (
            <button
              type="button"
              onClick={generatePrescription}
              disabled={isGenerating}
              className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 hover:text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowDownTrayIcon className={`w-3.5 h-3.5 ${isGenerating ? "animate-pulse" : ""}`} />
              {isGenerating ? "Generating…" : "Generate Prescription"}
            </button>
          )}
        </div>
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              placeholder={loadingCatalog ? "Loading lab tests..." : "Search lab tests..."}
              disabled={loadingCatalog}
              className="w-full pl-8 pr-3 py-2 text-[12px] text-slate-700 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-transparent bg-white disabled:bg-slate-50"
            />
          </div>

          {showDropdown && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {searchResults.length > 0 ? (
                searchResults.map((test) => (
                  <div
                    key={test._id}
                    onClick={() => handleSelectLabTest(test)}
                    className="px-4 py-2.5 hover:bg-teal-50 cursor-pointer flex items-center justify-between"
                  >
                    <div>
                      <div className="text-[12px] font-medium text-slate-800">{test.name}</div>
                      {test.client_friendly_name && (
                        <div className="text-[10px] text-slate-400">{test.client_friendly_name}</div>
                      )}
                      {test.sample_type && (
                        <div className="text-[10px] text-teal-600 font-medium">{test.sample_type}</div>
                      )}
                    </div>
                    <div className="text-[11px] font-semibold text-emerald-600">
                      {test.price != null ? `₹${Number(test.price).toFixed(2)}` : "—"}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-[11px] text-slate-400">
                  {loadingCatalog ? "Loading..." : "No lab tests found"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {labTests.length > 0 ? (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-slate-500 uppercase tracking-wider">Test Name</th>
                <th className="px-3 py-2 text-left font-bold text-slate-500 uppercase tracking-wider">Sample Type</th>
                <th className="px-3 py-2 text-left font-bold text-slate-500 uppercase tracking-wider">Price</th>
                <th className="px-3 py-2 text-left font-bold text-slate-500 uppercase tracking-wider">Action</th>
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {labTests.map((test) => (
                <tr key={test.id}>
                  <td className="px-3 py-2 font-medium text-slate-800">{test.name}</td>
                  <td className="px-3 py-2">
                    {test.sampleType ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                        {test.sampleType}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400">₹</span>
                      <input
                        type="number"
                        value={test.price ?? ""}
                        onChange={(e) => handlePriceChange(test.id, e.target.value)}
                        className="w-20 text-[11px] font-semibold text-emerald-600 border border-transparent hover:border-slate-200 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-transparent bg-transparent"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {test.uploading ? (
                      <span className="flex items-center gap-1.5 text-[10px] text-teal-600">
                        <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </span>
                    ) : test.fileUrl ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewFile(test.fileUrl)}
                          className="flex items-center gap-1 text-teal-600 hover:text-teal-700"
                        >
                          <DocumentTextIcon className="w-3.5 h-3.5" />
                          <span className="text-[10px] truncate max-w-[100px]">{test.fileName || "View"}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(test.id)}
                          className="text-slate-300 hover:text-red-400"
                          title="Remove report"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => triggerUpload(test.id)}
                        className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-teal-600 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                      >
                        <ArrowUpTrayIcon className="w-3 h-3" />
                        Upload Report
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => handleRemoveTest(test.id)}
                      className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 mb-3">
              <tr>
                <td colSpan={5} className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2 mb-2">
                    <span className="text-[11px] font-bold text-slate-500 uppercase">
                      Lab Tests Subtotal
                    </span>
                    <span className="text-[13px] font-bold text-emerald-600">
                      ₹{totalLabCost.toFixed(2)}
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-6 text-center">
          <BeakerIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-[11px] text-slate-400">No lab tests added yet.</p>
          <p className="text-[10px] text-slate-400">Search and select a lab test above to add it.</p>
        </div>
      )}

      {generateError && <p className="text-[11px] text-red-500">{generateError}</p>}
    </div>
  );
};

// ─── Objective Tabs ──────────────────────────────────────────────────────────

const OBJECTIVE_TABS = [
  { id: "examination", label: "Physical Examination", icon: "🔬" },
  { id: "labtests", label: "Lab Tests", icon: "🧪" },
];

// ─── Main Objective Component ──────────────────────────────────────────────

const Objective = forwardRef(({
  defaultOpen = false,
  isExpanded = true,
  onToggle,
  onDirtyChange,
  initialData,
  addManualItem,
  isHydratingRef,
}, ref) => {
  const [showAddVitals, setShowAddVitals] = useState(false);
  const [activeObjectiveTab, setActiveObjectiveTab] = useState("examination");

  const { labTests, medicationsList } = usePrescription();
  
  // ⭐ Get billing context for removing items
  const { billingItems, removeItem } = useBilling();

  // ─── Internal state (unrelated to the shared prescription) ────────────────
  const [vitals, setVitals] = useState(() => initialData?.vitals || []);
  const [examinationNotes, setExaminationNotes] = useState(() => initialData?.examinationNotes || "");
  const [examFindings, setExamFindings] = useState(() => initialData?.examFindings || {});
  const [attachments, setAttachments] = useState(() =>
    (initialData?.attachments || []).map((a) => ({
      id: a.attachmentId,
      name: a.name,
      label: a.label,
      type: a.fileType,
      url: a.storageUrl,
      extractedText: a.extractedText,
    }))
  );

  // ─── Function to remove lab test from billing ─────────────────────────────
  const removeItemFromBilling = useCallback((category, itemId) => {
    // Find the item in billing
    const items = billingItems?.[category] || [];
    const itemToRemove = items.find((item) => 
      item.catalogId === itemId || 
      item.lab_test_id === itemId ||
      item.id === itemId
    );
    
    if (itemToRemove) {
      removeItem(category, itemToRemove.id);
    }
  }, [billingItems, removeItem]);

  // ─── Dirty tracking ────────────────────────────────────────────────────────
  const [isDirty, setIsDirty] = useState(false);

  const markDirty = () => {
    setIsDirty(true);
    onDirtyChange?.(true);
  };

  const resetDirty = () => {
    setIsDirty(false);
    onDirtyChange?.(false);
  };

  useImperativeHandle(ref, () => ({
    getData: () => ({
      vitals,
      examinationNotes,
      examFindings,
      attachments,
      labTests,
    }),
    resetDirty,
    isDirty: () => isDirty,
  }));

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleAddVitals = (entry) => {
    const now = new Date();
    setVitals([
      ...vitals,
      {
        date: now.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        time: now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
        ...entry,
      }
    ]);
    markDirty();
  };

  const handleExaminationNotesChange = (val) => {
    setExaminationNotes(val);
    markDirty();
  };

  const handleExamFindingsChange = (key, val) => {
    setExamFindings({ ...examFindings, [key]: val });
    markDirty();
  };

  const skipLabMedDirtyCount = useRef(0);
  useEffect(() => {
    if (skipLabMedDirtyCount.current < 2) {
      skipLabMedDirtyCount.current += 1;
      return;
    }
    if (isHydratingRef?.current) return;
    markDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labTests, medicationsList]);

  const examSectionsWithData = Object.values(examFindings).filter((v) =>
    v && Object.values(v).some((x) => Array.isArray(x) ? x.length > 0 : !!x)
  ).length;

  const badge =
    vitals.length > 0 || examSectionsWithData > 0 || labTests.length > 0
      ? `${vitals.length} vitals${examSectionsWithData > 0 ? ` · ${examSectionsWithData} exam` : ""}${labTests.length > 0 ? ` · ${labTests.length} tests` : ""}`
      : null;

  return (
    <VetStartedSOAPSection
      id="soap-section-O"
      letter="O"
      title="Objective"
      subtitle="Vitals, physical examination & supporting data"
      accentBg="#f0fdf9"
      accentColor="#0f766e"
      badge={badge}
      defaultOpen={defaultOpen}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {showAddVitals && (
        <AddVitalsModal onClose={() => setShowAddVitals(false)} onSave={handleAddVitals} />
      )}

      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 w-fit">
        {OBJECTIVE_TABS.map((tab) => {
          const isActive = activeObjectiveTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              data-nav-tab="true"
              onClick={() => setActiveObjectiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold rounded-lg transition-colors ${
                isActive ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {tab.id === "labtests" && labTests.length > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  isActive ? "bg-teal-100 text-teal-700" : "bg-slate-200 text-slate-500"
                }`}>
                  {labTests.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeObjectiveTab === "examination" && (
        <>
          <div>
            <div className="flex items-center justify-between mb-2">
              <FieldLabel>Vitals</FieldLabel>
              <button
                onClick={() => setShowAddVitals(true)}
                className="flex items-center gap-1 text-[11px] font-bold text-teal-600 hover:text-teal-700"
              >
                <PlusIcon className="w-3.5 h-3.5" /> Record Vitals
              </button>
            </div>
            <VitalsTable rows={vitals} onAdd={() => setShowAddVitals(true)} />
          </div>

          <div className="mt-4">
            <NoteField
              label="General Examination Notes"
              value={examinationNotes}
              onChange={handleExaminationNotesChange}
              placeholder="Overall observations from the physical exam..."
              rows={3}
            />
          </div>

          <div className="mt-4">
            <div className="mb-2">
              <FieldLabel>Clinical Examination</FieldLabel>
            </div>
            <ClinicalExamTabs
              examFindings={examFindings}
              onExamFindingsChange={handleExamFindingsChange}
            />
          </div>
        </>
      )}

      {activeObjectiveTab === "labtests" && (
        <LabTestsSection
          markDirty={markDirty}
        />
      )}
    </VetStartedSOAPSection>
  );
});

Objective.displayName = "Objective";

export default Objective;